// Invite panel — the dialog the header's invite button opens (and the canvas
// MembersNavbar mounts inline): invite a person by email at a role, copy the
// link of the invitation just sent, and see the first members.
//
// Redesigned 2026-10-08 (Nika: "fix/redesign ui of invite user dropdown from
// top navbar"). Measured on dev before the change: a magic-link row that
// opened the CUSTOM-DOMAIN flow when clicked (it inherited DomainItem's
// onClick), a copy button that minted a second invitation, an email field
// narrower than its role select, form controls hung past the panel edge by a
// bleed, member rows printing slugs over three lines, an empty right side on
// every read-only row, a bottom fade that dimmed the last row and "All
// members" from the moment the panel opened, `role="menu"` with no menu
// items, and focus parked on the panel instead of the email field.
//
// THE SHAPE (top to bottom, gap not lines — DESIGN_LANGUAGE §7.7):
//   Title             the dialog's name, also its accessible name
//   the invite form   email (AppField) · role select · copy · send
//                     (InviteFormInDropdown — hidden when the caller may not
//                     invite here)
//   MagicLinkInInvite "Share by magic link": role select · "Copy magic link"
//                     (secondary — send stays the one primary), a status line,
//                     the org's active links with Revoke (INVITE-MAGIC-LINK-1,
//                     2026-10-09; org context only, same who-may-invite rule)
//   NoInviteNote      the one-line reason when the form is hidden
//   MembersInInvite   the first members, their roles, "All members"
//
// It is a DIALOG, not a menu: it holds a form. It keeps the house MenuPanel
// surface (the dialog ground, the overlay elevation, the card radius, the
// height cap) and swaps the menu semantics: `role="dialog"` with a name, and
// focus lands on the email field when the dropdown layer hosts it. Escape
// belongs to the layer (it closes and returns focus to the trigger). MenuPanel
// keeps its arrow-key handler; it only moves between `menuitem` rows and this
// panel has none, so the arrow keys stay the fields' own.
//
// THE RULES live in ONE function: `invitePolicy` (shared/functions/
// inviteRoles.js) — may this caller invite, at which roles, whose role may
// they change. Nothing here restates a role table.
export const InviteDropdown = {
  extends: 'MenuPanel',
  role: 'dialog',
  ariaLabel: (el) => el.call('polyglot', 'members.inviteDropdown.title'),

  // The dropdown OWNS the invite-form model: a NON-EMPTY store that declares
  // every key the form writes. An empty `state: {}` is ignored at runtime and
  // the form's writes then land on the ROOT state (2026-08-06: a `set` on
  // that chain wiped the whole session). Every write below is `update` with
  // explicit values — never `set`.
  //   email / role       the form's two fields (role '' = the policy default)
  //   status             'idle' | 'sending'
  //   error              a polyglot KEY for the email field's inline error
  //   failure            the send's error text (already user-facing)
  //   sentEmail/sentUrl  the invitation just created, and its accept link
  //   sentNoEmail        the server created it but the email did not go out
  //   copied             the link of that invitation is on the clipboard
  state: {
    email: '',
    role: '',
    status: 'idle',
    error: '',
    failure: '',
    sentEmail: '',
    sentUrl: '',
    sentNoEmail: false,
    copied: false
  },

  width: 'G2',
  padding: 'A',
  gap: 'A',
  textAlign: 'start',
  onClick: null,

  // Focus INTO the dialog, onto its first field: the email input, or — for a
  // caller with no invite form — the first link. Only when the dropdown layer
  // hosts the panel (its render IS the open edge); the inline MembersNavbar
  // mount sits on a page and must not pull the page's focus.
  onRender: (el) => {
    if (typeof el.lookup !== 'function' || !el.lookup('DropdownRoot')) return
    el.call('armMenuFocus', el, {
      selector: 'input[type="email"], a[href]',
      when: (node) => el.call('menuNodeVisible', node)
    })
  },

  Title: {
    tag: 'h2',
    margin: '0',
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'title',
    text: '{{ members.inviteDropdown.title | polyglot }}'
  },

  InviteFormInDropdown: {},

  MagicLinkInInvite: {},

  // 3(b): a caller the form is hidden from learns why, in one line.
  NoInviteNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z1',
    color: 'caption',
    hide: (el, s) => !el.call('invitePolicy', s).reason,
    text: (el, s) => {
      const reason = el.call('invitePolicy', s).reason
      if (!reason) return ''
      return el.call(
        'polyglot',
        reason === 'personalOrg'
          ? 'members.inviteDropdown.personalOrg'
          : 'members.inviteDropdown.noPermission'
      )
    }
  },

  MembersInInvite: {}
}
